/**
 * DOCUMENTATION:
 * Component: CssPracticalProject
 * Purpose: A capstone educational page demonstrating how to synthesize CSS concepts into a real-world project.
 * * * Note for Junior Developers:
 * - This project builds a "Responsive Pricing Component" — a classic frontend interview test.
 * - We use CSS Grid for the macro-layout (the 3 cards side-by-side) and Flexbox for the micro-layout (aligning text inside each card).
 * - BEM naming combined with SCSS nesting keeps our styles strictly scoped and prevents global leaks.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
        padding: "15px",
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    textTransform: "uppercase",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function CssPracticalProject() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* UI State */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    /* Scroll Listener for FAB */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* Copy to Clipboard Handler */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ---------------- DATA DEFINITIONS ---------------- */

    // Project Requirements Checklist
    const projectRequirements = useMemo(() => [
        { feature: "Macro-Layout (CSS Grid)", description: "Use CSS Grid with the RAM pattern to create a responsive 3-column layout without media queries." },
        { feature: "Micro-Layout (Flexbox)", description: "Use Flexbox inside each card to vertically align the pricing headers, lists, and buttons." },
        { feature: "BEM Naming Standard", description: "Implement strict Block Element Modifier architecture to prevent styling side-effects." },
        { feature: "Hardware-Accelerated Animation", description: "Add a smooth hover state using `transform: translateY()` and `box-shadow`." },
    ], []);

    // Coding examples demonstrating the synthesized project
    const codingModules = useMemo(() => [
        {
            title: "Phase 1: HTML Architecture (BEM)",
            code: `\n<section class="pricing-grid">\n\n  \n  <article class="pricing-card">\n    \n    <header class="pricing-card__header">\n      <h3 class="pricing-card__title">Basic</h3>\n      <p class="pricing-card__price">$9<span>/mo</span></p>\n    </header>\n    \n    \n    <ul class="pricing-card__features">\n      <li>1 User</li>\n      <li>10GB Storage</li>\n    </ul>\n\n    \n    <button class="btn btn--outline">Get Started</button>\n  </article>\n\n  \n  <article class="pricing-card pricing-card--featured">\n    \n    <button class="btn btn--primary">Get Started</button>\n  </article>\n\n</section>`,
            desc: "Notice the strictly scoped class names. The 'pricing-grid' is the container, 'pricing-card' is our block, and we use a '--featured' modifier to highlight the middle card."
        },
        {
            title: "Phase 2: Macro-Layout & SCSS Variables",
            code: `// _variables.scss\n$color-primary: goldenrod;\n$color-bg-dark: #100720;\n$color-text-light: #f5f5f5;\n\n// main.scss\n* {\n  box-sizing: border-box;\n}\n\n.pricing-grid {\n  display: grid;\n  // The Magic RAM Pattern: Responsive without Media Queries!\n  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));\n  gap: 2rem;\n  padding: 4rem 2rem;\n  align-items: center; // Vertically centers the cards\n}`,
            desc: "We apply `box-sizing: border-box` globally to protect our layout. Then, we use the Grid RAM pattern so the cards stack on mobile and sit side-by-side on desktop effortlessly."
        },
        {
            title: "Phase 3: Micro-Layout & Animation (SCSS Nesting)",
            code: `.pricing-card {\n  // Micro-layout: Stack items vertically\n  display: flex;\n  flex-direction: column;\n  padding: 2rem;\n  background: $color-bg-dark;\n  border: 1px solid rgba(255, 255, 255, 0.1);\n  border-radius: 12px;\n  \n  // Hardware-accelerated animation prep\n  transition: transform 0.3s ease, box-shadow 0.3s ease;\n\n  &:hover {\n    transform: translateY(-10px);\n    box-shadow: 0 15px 30px rgba(218, 165, 32, 0.2);\n  }\n\n  // Using BEM with SCSS '&'\n  &__header {\n    text-align: center;\n    margin-bottom: 2rem;\n  }\n\n  // The Featured Modifier\n  &--featured {\n    border-color: $color-primary;\n    transform: scale(1.05);\n    \n    // Ensure the hover state stacks correctly on the featured card\n    &:hover {\n      transform: scale(1.05) translateY(-10px);\n    }\n  }\n}`,
            desc: "We use Flexbox `flex-direction: column` inside the card to manage the spacing of its contents. The SCSS nesting allows us to write the BEM elements (`__header`) and modifiers (`--featured`) cleanly."
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    PRACTICAL UI PROJECT
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* ✅ Theory Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Synthesizing CSS Engineering
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            A beautiful design is worthless if the code behind it is fragile. In this project, we are building a "Responsive Pricing Component." This requires us to separate our layout concerns: we use <strong>CSS Grid</strong> to handle the macro-layout (positioning the cards relative to the page) and <strong>Flexbox</strong> to handle the micro-layout (positioning the text inside the cards). We wrap it all in strict <strong>BEM architecture</strong> via SCSS.
                        </Typography>

                        {/* ✅ Project Requirements Checklist */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Project Scope & Specifications</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Feature</div>
                                    <div>Engineering Strategy</div>
                                </ResponsiveDataRow>
                            )}
                            {projectRequirements.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod" }}>{item.feature}</Box>
                                    <Box sx={{ opacity: 0.9, fontSize: "0.9rem", lineHeight: 1.5 }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Coding Modules Section */}
                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            {/* Module Header & Action Buttons */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, lineHeight: 1.6 }}>{item.desc}</Typography>

                            {/* Code Block Presentation */}
                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/css-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
